import XIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  Slide,
  Stack,
  Switch,
  Typography,
} from "@mui/material"
import { DialogProps } from "./types"
import { forwardRef, useState } from "react"
import { useEventContext } from "@/contexts/EventContext"
import useIsMobile from "@/hooks/useIsMobile"
import { TransitionProps } from "@mui/material/transitions"
import ReactGA from "react-ga4"
import { useGetMe } from "@/hooks/useGetMe"

interface EventFilterDialogProps extends DialogProps {}

const filters = {
  ageFilters: [
    { id: 1, filterName: "Any", filterValue: "ANY" },
    { id: 2, filterName: "Adult", filterValue: "ADULT" },
    { id: 3, filterName: "Family", filterValue: "FAMILY" },
    { id: 4, filterName: "Teen", filterValue: "TEEN" },
  ],
  priceFilters: [
    { id: 5, filterName: "Any", filterValue: "Any" },
    { id: 6, filterName: "Free", filterValue: "free" },
    { id: 7, filterName: "$", filterValue: "$" },
    { id: 8, filterName: "$$", filterValue: "$$" },
    { id: 9, filterName: "$$$", filterValue: "$$$" },
    // { id: 10, filterName: "$$$$" },
  ],
  timeFilters: [
    { id: 11, filterName: "Any" },
    { id: 12, filterName: "Morning" },
    { id: 13, filterName: "Afternoon" },
    { id: 14, filterName: "Evening" },
    { id: 15, filterName: "Night" },
  ],
}

const ToggleButton = ({
  isOn,
  setIsOn,
}: {
  isOn: boolean
  setIsOn: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { setPetFriendlyFilter } = useEventContext()
  const { data: me } = useGetMe()

  const toggleButton = () => {
    const newValue = !isOn
    setIsOn((prevState) => !prevState)
    setPetFriendlyFilter(String(newValue))

    // Track pet friendly filter toggle
    ReactGA.event({
      category: "Filter",
      action: newValue ? "Enable pet friendly filter" : "Disable pet friendly filter",
      label: `Pet Friendly: ${newValue ? "ON" : "OFF"} | User: ${me?.id ?? "guest"}`,
    })
  }

  return (
    <FormControlLabel
      control={<Switch checked={isOn} onChange={toggleButton} />}
      label={isOn ? "ON" : "OFF"}
    />
  )
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<unknown, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function EventFilterDialog({
  isOpen,
  closeDialog,
}: EventFilterDialogProps) {
  const { data: me } = useGetMe()

  function handleClose() {
    closeDialog()
  }
  function handleApplyClose() {
    // Track apply filters
    ReactGA.event({
      category: "Filter",
      action: "Apply filters",
      label: `Age: ${filters.ageFilters.find(f => f.id === ageButton)?.filterValue || "ANY"} | Price: ${filters.priceFilters.find(f => f.id === priceButton)?.filterValue || "Any"} | Pet Friendly: ${isOn ? "ON" : "OFF"} | User: ${me?.id ?? "guest"}`,
    })

    setTimeout(() => {
      closeDialog()
    }, 100)
  }
  function handleResetFilters() {
    // Track reset filters
    ReactGA.event({
      category: "Filter",
      action: "Reset filters",
      label: `User: ${me?.id ?? "guest"}`,
    })

    setAgeButton(1)
    setAgeRangeFilter("ANY")
    setPriceButton(5)
    setPriceFilter("Any")
    setTimeButton(11)
    setIsOn(false)
    setPetFriendlyFilter("false")
  }

  const isMobile = useIsMobile()
  const { setAgeRangeFilter, setPriceFilter, setPetFriendlyFilter } =
    useEventContext()

  const [ageButton, setAgeButton] = useState<number | null>(1)
  const [priceButton, setPriceButton] = useState<number | null>(5)
  const [timeButton, setTimeButton] = useState<number | null>(11)
  const [isOn, setIsOn] = useState(false)

  const handleAgeClick = (index: number, filterValue: string) => {
    setAgeButton(index)
    setAgeRangeFilter(filterValue)

    // Track age filter click
    ReactGA.event({
      category: "Filter",
      action: "Select age filter",
      label: `${filterValue} | User: ${me?.id ?? "guest"}`,
    })
  }
  const handlePriceClick = (index: number, filterValue: string) => {
    setPriceButton(index)
    setPriceFilter(filterValue)

    // Track price filter click
    ReactGA.event({
      category: "Filter",
      action: "Select price filter",
      label: `${filterValue} | User: ${me?.id ?? "guest"}`,
    })
  }
  const handleTimeClick = (index: number) => {
    setTimeButton(index)

    // Track time filter click
    const filterName = filters.timeFilters.find(f => f.id === index)?.filterName || "Unknown"
    ReactGA.event({
      category: "Filter",
      action: "Select time filter",
      label: `${filterName} | User: ${me?.id ?? "guest"}`,
    })
  }

  const filterSectionStyle = {
    display: "flex",
    flexDirection: "column",
    padding: "1rem",
    color: "black",
  }

  const buttonDiv = {
    display: "flex",
    marginTop: ".5rem",
  }

  const dividerStyle = {
    marginTop: ".5rem",
    marginBottom: ".5rem",
  }

  const ageButtonStyling = (index: number) => ({
    marginRight: ".5rem",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    paddingTop: ".3rem",
    paddingBottom: ".3rem",
    borderRadius: "2em",
    backgroundColor: ageButton === index ? "#000" : "#FFF",
    color: ageButton === index ? "#FFF" : "#000",
    border: "1px solid black",
    fontWeight: isMobile ? "400" : "600",
  })

  const priceButtonStyling = (index: number) => ({
    marginRight: ".5rem",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    paddingTop: ".3rem",
    paddingBottom: ".3rem",
    borderRadius: "2em",
    backgroundColor: priceButton === index ? "#000" : "#FFF",
    color: priceButton === index ? "#FFF" : "#000",
    border: "1px solid black",
    fontWeight: isMobile ? "400" : "600",
  })
  const timeButtonStyling = (index: number) => ({
    marginRight: ".5rem",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    paddingTop: ".3rem",
    paddingBottom: ".3rem",
    borderRadius: "2em",
    backgroundColor: timeButton === index ? "#000" : "#FFF",
    color: timeButton === index ? "#FFF" : "#000",
    border: "1px solid black",
    fontWeight: isMobile ? "400" : "600",
  })
  const titleStyling = { marginBottom: ".5em" }
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{
        sx: isMobile
          ? {
              position: "absolute",
              bottom: 34,
              borderRadius: "16px 16px 0 0",
              maxHeight: "80vh",
            }
          : {}, // Bottom-sheet style for mobile
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        minWidth={isMobile ? "90%" : "500px"}
        pl={3}
        pr={1}
        py={1}
        sx={{
          backgroundColor: "white",
          //   color: "primary.contrastText",
          textAlign: "center",
        }}
      >
        <Typography>Filters</Typography>
        <Box>
          <IconButton color="inherit" onClick={handleClose}>
            <XIcon />
          </IconButton>
        </Box>
      </Stack>
      <Divider />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? 0 : "1rem",
          color: "black",
        }}
      >
        <Box sx={filterSectionStyle}>
          <Typography variant="h4" style={{ marginBottom: ".5em" }}>
            Age
          </Typography>
          <Typography variant="body1">Events designed just for you</Typography>
          <div style={{ display: "flex", marginTop: ".5rem" }}>
            {filters.ageFilters.map((filter) => (
              <Button
                key={filter.id}
                style={ageButtonStyling(filter.id)}
                onClick={() => handleAgeClick(filter.id, filter.filterValue)}
              >
                {filter.filterName}
              </Button>
            ))}
          </div>
        </Box>
        <Divider sx={dividerStyle} />
        <Box sx={filterSectionStyle}>
          <Typography variant="h4" style={titleStyling}>
            Price
          </Typography>
          <Typography variant="body1">
            Relative based on the cost of other events
          </Typography>
          <div style={buttonDiv}>
            {filters.priceFilters.map((filter) => (
              <Button
                style={priceButtonStyling(filter.id)}
                onClick={() => handlePriceClick(filter.id, filter.filterValue)}
                key={filter.id}
              >
                {filter.filterName}
              </Button>
            ))}
          </div>
        </Box>
        <Divider sx={dividerStyle} />
        <Box sx={filterSectionStyle}>
          <Typography variant="h4" style={titleStyling}>
            Time
          </Typography>
          <Typography variant="body1">
            Includes events that start or finish in the selected time
          </Typography>
          <div style={buttonDiv}>
            {filters.timeFilters.map((filter) => (
              <Button
                style={timeButtonStyling(filter.id)}
                onClick={() => handleTimeClick(filter.id)}
                key={filter.id}
              >
                {filter.filterName}
              </Button>
            ))}
          </div>
        </Box>
        <Divider sx={dividerStyle} />
        <Box sx={filterSectionStyle}>
          <Typography variant="h4" style={titleStyling}>
            Pet Friendly
          </Typography>
          <Typography variant="body1">
            Toggle to only see places that allow your furry friends
          </Typography>
          <ToggleButton isOn={isOn} setIsOn={setIsOn} />
          <Divider style={dividerStyle} />
          {/* <Typography variant="h5" style={{ color: "#147BE1" }}>
            Advanced
          </Typography> */}
          <Stack direction={"row"} justifyContent={"end"}>
            <Button onClick={handleResetFilters}>Reset Filters</Button>
            <Button variant="contained" onClick={handleApplyClose}>
              Apply Filters
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  )
}
